import { HTMLAttributes, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { forEach } from 'lodash'

import { cn } from '@/lib/utils'

export type LogoProps = HTMLAttributes<HTMLSpanElement> &
  Partial<{
    loop: boolean
  }>

const Logo = ({ loop = false, className, ...props }: LogoProps) => {
  const root = useRef<HTMLSpanElement>(null)

  useGSAP(
    ({ selector }) => {
      if (!loop) return

      const points = selector?.('[data-logo-point]')
      const group = selector?.('[data-logo-points]')

      const keyframes = [
        [
          { x: 5, y: 0 },
          { x: 0, y: 0 }
        ],
        [
          { x: 0, y: 5 },
          { x: 0, y: 0 }
        ],
        [
          { x: -5, y: 0 },
          { x: 0, y: 0 }
        ],
        [
          { x: 0, y: -5 },
          { x: 0, y: 0 }
        ]
      ]
      const timeline = gsap
        .timeline({ repeat: -1 })
        .to(group, { rotation: 90, duration: 0.6, ease: 'none' })

      forEach(points, (point, index: number) => {
        timeline.to(
          point,
          {
            keyframes: keyframes[index],
            duration: 0.6,
            ease: 'power1.out'
          },
          0
        )
      })
    },
    { scope: root, dependencies: [loop], revertOnUpdate: true }
  )

  return (
    <span ref={root} className={cn('relative', className)} {...props}>
      {loop ? (
        <span className="absolute left-0 top-0 h-[2.5rem] w-[2.4rem]" data-logo-points>
          <span className="absolute left-1/2 top-1/2 size-[0.6rem] -translate-x-1/2 -translate-y-1/2">
            <span className="absolute left-0 top-0 size-full rounded-full bg-current" />
          </span>
          <span className="absolute left-0 top-1/2 size-[0.6rem] -translate-y-1/2 translate-x-0">
            <span
              data-logo-point
              className="absolute left-0 top-0 size-full rounded-full bg-current"
            />
          </span>
          <span className="absolute left-1/2 top-0 size-[0.6rem] -translate-x-1/2 translate-y-0">
            <span
              data-logo-point
              className="absolute left-0 top-0 size-full rounded-full bg-current"
            />
          </span>
          <span className="absolute right-0 top-1/2 size-[0.6rem] -translate-y-1/2 translate-x-0">
            <span
              data-logo-point
              className="absolute left-0 top-0 size-full rounded-full bg-current"
            />
          </span>
          <span className="absolute bottom-0 left-1/2 size-[0.6rem] -translate-x-1/2 translate-y-0">
            <span
              data-logo-point
              className="absolute left-0 top-0 size-full rounded-full bg-current"
            />
          </span>
        </span>
      ) : null}

      <svg
        width="108"
        height="25"
        viewBox="0 0 108 25"
        xmlns="http://www.w3.org/2000/svg"
        className="fill-current"
      >
        {!loop ? (
          <g>
            <path d="M11.8877 15.6227C13.4977 15.6227 14.8029 14.3175 14.8029 12.7075C14.8029 11.0974 13.4977 9.79222 11.8877 9.79222C10.2776 9.79222 8.9724 11.0974 8.9724 12.7075C8.9724 14.3175 10.2776 15.6227 11.8877 15.6227Z" />
            <path d="M2.85908 15.5104C4.43811 15.5104 5.71816 14.2303 5.71816 12.6513C5.71816 11.0723 4.43811 9.79222 2.85908 9.79222C1.28005 9.79222 0 11.0723 0 12.6513C0 14.2303 1.28005 15.5104 2.85908 15.5104Z" />
            <path d="M21.1409 15.5104C22.7199 15.5104 24 14.2303 24 12.6513C24 11.0723 22.7199 9.79222 21.1409 9.79222C19.5619 9.79222 18.2818 11.0723 18.2818 12.6513C18.2818 14.2303 19.5619 15.5104 21.1409 15.5104Z" />
            <path d="M11.9438 24.8198C13.5849 24.8198 14.9153 23.4895 14.9153 21.8484C14.9153 20.2073 13.5849 18.877 11.9438 18.877C10.3028 18.877 8.9724 20.2073 8.9724 21.8484C8.9724 23.4895 10.3028 24.8198 11.9438 24.8198Z" />
            <path d="M11.8877 6.65033C13.4977 6.65033 14.8029 5.34513 14.8029 3.73508C14.8029 2.12503 13.4977 0.819824 11.8877 0.819824C10.2776 0.819824 8.9724 2.12503 8.9724 3.73508C8.9724 5.34513 10.2776 6.65033 11.8877 6.65033Z" />
          </g>
        ) : null}
        <g>
          <path d="M55.2991 20.8392H44.2831C42.2492 20.8392 40.3354 20.0469 38.8981 18.6077C37.4589 17.1685 36.6667 15.2566 36.6667 13.2227V5.46099C36.6667 5.09682 36.963 4.80045 37.3272 4.80045C37.6914 4.80045 37.9877 5.09682 37.9877 5.46099V13.2247C37.9877 14.906 38.6425 16.4867 39.8318 17.676C41.0211 18.8653 42.6018 19.5201 44.2831 19.5201H55.2991C55.6633 19.5201 55.9596 19.8164 55.9596 20.1806C55.9596 20.5448 55.6633 20.8411 55.2991 20.8411V20.8392Z" />
          <path d="M75.2681 20.8392H60.8313C60.4671 20.8392 60.1708 20.5428 60.1708 20.1787V5.46099C60.1708 5.09682 60.4671 4.80045 60.8313 4.80045H76.0759C80.2754 4.80045 83.6923 8.2174 83.6923 12.4169C83.6923 17.062 79.9132 20.8411 75.2681 20.8411V20.8392ZM61.4918 19.5181H75.2681C79.1848 19.5181 82.3693 16.3317 82.3693 12.415C82.3693 8.9438 79.5451 6.11958 76.0739 6.11958H61.4899V19.5162L61.4918 19.5181Z" />
          <path d="M88.5621 20.8392C88.1979 20.8392 87.9015 20.5428 87.9015 20.1787V12.415C87.9015 10.3811 88.6938 8.46728 90.133 7.02999C91.5722 5.59077 93.4841 4.79852 95.518 4.79852H106.534C106.898 4.79852 107.195 5.09488 107.195 5.45905C107.195 5.82321 106.898 6.11958 106.534 6.11958H95.518C93.8366 6.11958 92.256 6.7743 91.0667 7.96365C89.8773 9.153 89.2226 10.7336 89.2226 12.415V15.1404C89.4919 14.7104 89.7979 14.3191 90.1311 13.9859C91.5703 12.5467 93.4822 11.7544 95.5161 11.7544H106.532C106.896 11.7544 107.193 12.0508 107.193 12.415C107.193 12.7791 106.896 13.0755 106.532 13.0755H95.5161C93.8347 13.0755 92.2541 13.7302 91.0647 14.9196C89.9626 16.0218 89.2207 17.9743 89.2207 19.7738V20.1767C89.2207 20.5409 88.9243 20.8373 88.5601 20.8373L88.5621 20.8392Z" />
        </g>
      </svg>
    </span>
  )
}

export default Logo
